import { Button, EmptyState, ErrorState, LoadingState, cn } from '@lawbee/ui';
import { Link } from '@tanstack/react-router';
import { Link2Off, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { useSearchParam } from '../../lib/hooks';
import { PublicFrame } from '../../shell/RootPages';

/** True for "this token/slug does not exist" style errors. */
export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 410);
}

/** `?embed=1` / `?embed=true` drops the public chrome (iframe embeds). */
export function useEmbed(): boolean {
  const v = useSearchParam('embed');
  return v === '1' || v === 'true';
}

export function InvalidLink({ title = 'This link is not valid or has expired', description }: { title?: string; description?: ReactNode }) {
  return (
    <EmptyState
      icon={<Link2Off />}
      title={title}
      description={description ?? 'Links from law firms expire for your privacy. Please contact the firm and ask for a new link.'}
    />
  );
}

/**
 * Standard loading / not-found / error handling for a public page query. Returns a node to render
 * instead of the page, or null when the data is ready.
 */
export function publicQueryState(
  q: { isLoading: boolean; error: unknown; refetch: () => unknown },
  opts: { embed?: boolean; notFound?: ReactNode; loadingLabel?: string } = {},
): ReactNode {
  if (q.isLoading) {
    return (
      <PublicFrame embed={opts.embed}>
        <LoadingState label={opts.loadingLabel ?? 'Loading'} className="min-h-[50vh]" />
      </PublicFrame>
    );
  }
  if (q.error) {
    return (
      <PublicFrame embed={opts.embed}>
        {isNotFound(q.error) ? (opts.notFound ?? <InvalidLink />) : <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      </PublicFrame>
    );
  }
  return null;
}

export function ConfidentialityNotice({ notice, className, children }: { notice?: string | null; className?: string; children?: ReactNode }) {
  if (!notice && !children) return null;
  return (
    <div className={cn('flex gap-3 rounded-xl bg-muted/60 px-4 py-3.5 text-[13px] leading-relaxed text-muted-foreground', className)}>
      <ShieldCheck className="mt-0.5 size-4 shrink-0 text-foreground/70" aria-hidden />
      <div className="space-y-1.5">
        {notice ? <p className="whitespace-pre-line">{notice}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Large centred confirmation block used by thank-you screens. */
export function DoneBlock({ icon, title, children, action }: { icon: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-4 py-10 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-success/12 text-success [&_svg]:size-7">{icon}</div>
      <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.025em]">{title}</h1>
      {children ? <div className="mx-auto max-w-md space-y-3 text-[15px] leading-relaxed text-muted-foreground">{children}</div> : null}
      {action}
    </div>
  );
}

export function HomeLinkButton({ label = 'Go to LawBee' }: { label?: string }) {
  return (
    <Button asChild variant="outline">
      <Link to="/">{label}</Link>
    </Button>
  );
}

/** Add days to a YYYY-MM-DD date string (calendar arithmetic, no timezone drift). */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Format a YYYY-MM-DD calendar date without timezone shifts. */
export function formatCalendarDate(date: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', ...opts }).format(new Date(`${date}T12:00:00Z`));
}
